/* eslint-disable max-lines */
// deps
import {
    RegisterOverlaysArgs,
    ReviewedCartApiResponse,
    SelectiveCouponVariantXP,
    ValueDrivenCrossSellXP,
    ApplyCouponsWidgetInfo,
    GiftingCartApiResponse,
    GiftingCouponCode,
    GiftingApplyCouponWidgetInfo,
    CouponInfo,
    ICouponCard,
    IAppliedCouponCodes,
} from "@swiggy-private/cart-types";
import { format, isEmpty } from "@swiggy-private/js-utils";
import { getAppliedOfferIds } from "@cart-client/helpers/CouponListing";
import { CTA_TEXT } from "@swiggy-private/cart-ui";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";

// helpers
import { getSavingsBannerData } from "./Cart";
import { isValidNumber } from "@swiggy-private/core-ui";

// constants
import { APPLY_COUPON_TEXTS, CouponType } from "@cart-client/constants/Coupon";
import { VALID_RENDERING_DETAILS_TYPE } from "@swiggy-private/cart-ui";
import { CART_V2_APPLY_COUPON_ICON, CART_V2_APPLY_PAYMENT_COUPON_ICON } from "@cart-client/constants/index";
import { CouponsAnalyticsExtraContext, CouponsAnalyticsExtraParams } from "@cart-client/interfaces/Coupon";

/**
 * Takes 3 params and returns if it is direct coupon scenario or not via true/false.
 * @param {ReviewedCartApiResponse["apply_coupon_widget_info"]} applyCouponWidgetData
 * @param {SelectiveCouponVariantXP} xpSelectiveCouponVariant
 * @param {ValueDrivenCrossSellXP} ValueDrivenCrossSellXP
 *
 * if @param applyCouponWidgetData is not there then it is not direct coupon usecase
 * if @param valueDrivenCrossSellXPVariant is coupon locked widget and amount to be added exist in coupon data, then return true
 * if @param xpSelectiveCouponVariant is default variant and amount to be added does not exist in coupon data, then return true
 * return false in all other cases
 *
 * @returns {boolean} if it is direct coupon case or not
 */
export const isDirectCouponUsecase = (
    applyCouponWidgetData?: ReviewedCartApiResponse["apply_coupon_widget_info"] | GiftingApplyCouponWidgetInfo,
    xpSelectiveCouponVariant?: SelectiveCouponVariantXP,
    valueDrivenCrossSellXPVariant?: ValueDrivenCrossSellXP,
): boolean => {
    // for IPL we are getting applyCouponWidgetData as defined but with coupon_code null due to potential savings text comms
    // these potential savings text comms flow as IPL six hit can happen anytime hence comms needs to be available always
    // even if a coupon is not applicable for a user, we are getting applyCouponWidgetData as defined
    const isGiftingCartCouponEmpty = isEmpty((applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes);
    // both BAU coupon_code and new gifting cart coupon_codes should be empty
    if (applyCouponWidgetData == null || (applyCouponWidgetData?.coupon_code == null && isGiftingCartCouponEmpty)) {
        return false;
    }
    if (valueDrivenCrossSellXPVariant === ValueDrivenCrossSellXP.COUPON_LOCKED_WIDGET) {
        return !!applyCouponWidgetData.amount_to_add;
    }
    if (xpSelectiveCouponVariant == null || xpSelectiveCouponVariant === SelectiveCouponVariantXP.DEFAULT) {
        return !applyCouponWidgetData.amount_to_add;
    }
    return false;
};

/**
 * Takes 1 param and returns true/false.
 * @param {ApplyCouponsWidgetInfo["payment_coupon"]} applyPaymentCouponWidgetData
 *
 * if @param applyPaymentCouponWidgetData is not empty and if it consists coupon code and savings
 * then it returns true that tells to show direct payment coupon usecase
 * return false in all other cases
 */
export const isDirectPaymentCouponUsecase = (
    applyPaymentCouponWidgetData?: ApplyCouponsWidgetInfo["payment_coupon"],
): boolean => {
    return !isEmpty(applyPaymentCouponWidgetData?.savings) && !isEmpty(applyPaymentCouponWidgetData?.coupon_code);
};

/**
 * Takes 2 params and returns true/false.
 * @param {ReviewedCartApiResponse["apply_coupon_widget_info"]} applyCouponWidgetData
 * @param {SelectiveCouponVariantXP} xpSelectiveCouponVariant
 *
 * if @param xpSelectiveCouponVariant is not disable description variant and
 * amount to be added does not exist in @param applyCouponWidgetData, then return true
 * return false in all other cases
 *
 * @returns {boolean}
 */
export const canShowPotentialSavings = (
    applyCouponWidgetData?: ReviewedCartApiResponse["apply_coupon_widget_info"],
    xpSelectiveCouponVariant?: SelectiveCouponVariantXP,
): boolean => {
    return (
        xpSelectiveCouponVariant !== SelectiveCouponVariantXP.DISABLE_DESCRIPTION &&
        !isEmpty(applyCouponWidgetData) &&
        !applyCouponWidgetData.amount_to_add
    );
};

export const getPotentialSavingsText = (
    cartResponse: ReviewedCart | null,
    applyCouponWidgetData?: ReviewedCartApiResponse["apply_coupon_widget_info"],
    xpSelectiveCouponVariant?: SelectiveCouponVariantXP,
): string => {
    if (!canShowPotentialSavings(applyCouponWidgetData, xpSelectiveCouponVariant)) {
        return "";
    }

    if (!applyCouponWidgetData?.coupon_code || applyCouponWidgetData?.savings) {
        return "";
    }

    const hasNonCouponSavings = !!getSavingsBannerData(cartResponse);
    const commsString = hasNonCouponSavings
        ? APPLY_COUPON_TEXTS.SAVE_ANOTHER_X_WITH_COUPON
        : APPLY_COUPON_TEXTS.SAVE_X_WITH_COUPON;

    return format(commsString, applyCouponWidgetData?.savings, applyCouponWidgetData?.coupon_code);
};

export const getPotentialSavingsTextsForGiftingCart = (
    applyCouponWidgetData: GiftingCartApiResponse["apply_coupon_widget_info"],
): string => {
    if (
        isEmpty(applyCouponWidgetData?.coupon_codes) ||
        isEmpty(applyCouponWidgetData?.coupon_codes[0]?.code) ||
        isEmpty(applyCouponWidgetData?.savings)
    ) {
        return "";
    }
    // picking savings from backend response as they are already formatted
    const savings = applyCouponWidgetData?.savings;
    const { count } = getCombinedSavingsAndCountForGiftingCoupons(applyCouponWidgetData?.coupon_codes);
    if (count === 1) {
        return format(APPLY_COUPON_TEXTS.SAVE_X_WITH_COUPON, savings, applyCouponWidgetData?.coupon_codes[0]?.code);
    } else if (count > 1) {
        return format(APPLY_COUPON_TEXTS.SAVE_X_WITH_Y_COUPON_CODES, savings, count);
    }

    return "";
};

/**
 * Takes 1 param and returns true/false.
 * @param {ReviewedCartApiResponse["apply_coupon_widget_info"]} applyCouponWidgetData
 *
 * if @param applyCouponWidgetData is not empty and if it consists coupon code and amount to be added
 * then it returns true that tells to show unlock coupon comms in direct coupon locked widget usecase
 * return false in all other cases
 *
 * @returns {boolean}
 */
export const canShowUnlockCouponComms = (
    applyCouponWidgetData?: ReviewedCartApiResponse["apply_coupon_widget_info"],
) => {
    return (
        !isEmpty(applyCouponWidgetData) &&
        !isEmpty(applyCouponWidgetData.coupon_code) &&
        !isEmpty(applyCouponWidgetData.amount_to_add)
    );
};

export const getCouponExpiryInDays = (expiryInMs: string): number => {
    const oneDayInMs = 1000 * 60 * 60 * 24;
    const expiryTimeInMs = Number(expiryInMs) > 0 ? Number(expiryInMs) : 0;

    return expiryTimeInMs / oneDayInMs;
};

export const getHighestElement = (queue: RegisterOverlaysArgs[]): RegisterOverlaysArgs | null => {
    if (isEmpty(queue)) {
        return null;
    }

    return (queue as RegisterOverlaysArgs[])?.reduce((prev, curr) => {
        return prev.priority < curr.priority ? prev : curr;
    });
};

export const isPaymentCouponUsecase = (type: string) => {
    return [
        VALID_RENDERING_DETAILS_TYPE.PAYMENT_COUPON_BUTTON.toLowerCase(),
        VALID_RENDERING_DETAILS_TYPE.PAYMENT_COUPON_REMOVE_BUTTON.toLowerCase(),
    ].includes(type.toLowerCase());
};

export const getFallbackCouponIcon = (type: string) => {
    if (isPaymentCouponUsecase(type)) {
        return CART_V2_APPLY_PAYMENT_COUPON_ICON;
    }

    return CART_V2_APPLY_COUPON_ICON;
};

export const getAppliedPaymentCouponTitleText = (
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    paymentCouponCode?: string,
) => {
    let titleText = "";
    if (
        !isEmpty(applyPaymentCouponWidgetData) &&
        !isEmpty(applyPaymentCouponWidgetData.savings) &&
        !isEmpty(paymentCouponCode)
    ) {
        titleText = format(
            APPLY_COUPON_TEXTS.X_SAVED_WITH_COUPON,
            applyPaymentCouponWidgetData.savings,
            paymentCouponCode,
        );
    } else {
        titleText = format(APPLY_COUPON_TEXTS.X_APPLIED, paymentCouponCode);
    }
    return titleText;
};

export const getAppliedRegularCouponTitleText = (
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
    couponCode?: string,
    giftingCouponCodes?: GiftingCouponCode[],
) => {
    let titleText = "";
    if (!isEmpty(applyCouponWidgetData) && !isEmpty(applyCouponWidgetData.savings)) {
        if (!isEmpty(couponCode) || (!isEmpty(giftingCouponCodes?.[0]?.code) && giftingCouponCodes?.length === 1)) {
            titleText = format(
                APPLY_COUPON_TEXTS.X_SAVED_WITH_COUPON,
                applyCouponWidgetData.savings,
                couponCode || giftingCouponCodes?.[0]?.code,
            );
            // used for gifting cart with multiple coupon codes
        } else if (!isEmpty(giftingCouponCodes) && giftingCouponCodes?.length > 1) {
            // use savings from applyCouponWidgetData as they are already formatted
            const savings = applyCouponWidgetData?.savings;
            const { count } = getCombinedSavingsAndCountForGiftingCoupons(giftingCouponCodes);
            titleText = format(APPLY_COUPON_TEXTS.X_SAVED_WITH_Y_COUPON_CODES, savings, count);
        }
    } else {
        titleText = format(APPLY_COUPON_TEXTS.X_APPLIED, couponCode);
    }
    return titleText;
};

export const getAppliedCouponTitleText = ({
    couponType,
    applyCouponWidgetData,
    applyPaymentCouponWidgetData,
    couponCode,
    paymentCouponCode,
    giftingCouponCodes,
}: {
    couponType: CouponType;
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"];
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"];
    couponCode?: string;
    paymentCouponCode?: string;
    giftingCouponCodes?: GiftingCouponCode[];
}) => {
    if (couponType === CouponType.PAYMENT) {
        return getAppliedPaymentCouponTitleText(applyPaymentCouponWidgetData, paymentCouponCode);
    }
    return getAppliedRegularCouponTitleText(applyCouponWidgetData, couponCode, giftingCouponCodes);
};

export const getAppliedCouponSubtitleText = (couponType: CouponType, isNewPORFlow: boolean) => {
    if (couponType === CouponType.PAYMENT) {
        return APPLY_COUPON_TEXTS.VIEW_ALL_PAYMENT_COUPONS;
    }
    return isNewPORFlow ? APPLY_COUPON_TEXTS.VIEW_ALL_RESTAURANT_COUPONS : APPLY_COUPON_TEXTS.VIEW_ALL_COUPONS;
};

export const getAppliedCouponIcon = (couponType: CouponType) => {
    if (couponType === CouponType.PAYMENT) {
        return CART_V2_APPLY_PAYMENT_COUPON_ICON;
    }
    return CART_V2_APPLY_COUPON_ICON;
};

export const getApplyRestaurantCouponText = (
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
) => {
    if (!isEmpty(applyCouponWidgetData?.savings)) {
        if (!isEmpty(applyCouponWidgetData?.extra_savings)) {
            return format(
                APPLY_COUPON_TEXTS.SAVE_ON_ORDER_WITH_EXTRA_SAVINGS,
                applyCouponWidgetData?.savings,
                applyCouponWidgetData?.extra_savings,
            );
        }
        return format(APPLY_COUPON_TEXTS.SAVE_ON_ORDER, applyCouponWidgetData?.savings);
    }
    return APPLY_COUPON_TEXTS.APPLY_COUPON;
};

export const getApplyPaymentCouponText = (applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"]) => {
    if (!isEmpty(applyPaymentCouponWidgetData?.savings) && !isEmpty(applyPaymentCouponWidgetData?.coupon_code)) {
        return format(
            APPLY_COUPON_TEXTS.SAVE_X_WITH_COUPON,
            applyPaymentCouponWidgetData?.savings,
            applyPaymentCouponWidgetData?.coupon_code,
        );
    } else if (!isEmpty(applyPaymentCouponWidgetData?.savings)) {
        return format(APPLY_COUPON_TEXTS.SAVE_ON_ORDER, applyPaymentCouponWidgetData?.savings);
    }
    return APPLY_COUPON_TEXTS.APPLY_COUPON;
};

export const getApplyCouponTextForGiftingCart = (applyCouponWidgetData: GiftingApplyCouponWidgetInfo) => {
    const couponCodes = (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes;
    // picking savings from backend response as they are already formatted
    const savings = applyCouponWidgetData?.savings;
    if (!isEmpty(couponCodes) && !isEmpty(couponCodes?.[0]?.code) && !isEmpty(savings)) {
        const { count } = getCombinedSavingsAndCountForGiftingCoupons(
            (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
        );
        if (count > 1) {
            return format(APPLY_COUPON_TEXTS.SAVE_X_WITH_Y_COUPON_CODES, savings, count);
        }
        return format(APPLY_COUPON_TEXTS.SAVE_X_WITH_COUPON, savings, couponCodes[0]?.code);
    }
    return APPLY_COUPON_TEXTS.APPLY_COUPON;
};

export const getApplyCouponText = (
    couponType: CouponType,
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
) => {
    if (couponType === CouponType.PAYMENT) {
        return getApplyPaymentCouponText(applyPaymentCouponWidgetData);
    }
    return getApplyRestaurantCouponText(applyCouponWidgetData);
};

export const getApplyCouponWidgetData = (
    cartResponse: ReviewedCart | null,
): ReviewedCartApiResponse["apply_coupon_widget_info"] | ApplyCouponsWidgetInfo["restaurant_coupon"] => {
    return (
        cartResponse?.data?.apply_coupon_widget_info || cartResponse?.data?.apply_coupons_widget_info?.restaurant_coupon
    );
};

export const getCouponlessSavingWidgetData = (
    cartResponse: ReviewedCart | null,
): ReviewedCartApiResponse["couponless_savings_widget_info"] => {
    return cartResponse?.data?.couponless_savings_widget_info;
};

export const getApplyPaymentCouponWidgetData = (
    cartResponse: ReviewedCart | null,
): ApplyCouponsWidgetInfo["payment_coupon"] => {
    return cartResponse?.data?.apply_coupons_widget_info?.payment_coupon;
};

export const getDiscountingToastMessage = (cartData?: ReviewedCartApiResponse | null): string | undefined =>
    cartData?.coupon_details?.toast_message;

export const getCombinedSavingsAndCountForGiftingCoupons = (couponCodes?: GiftingCartApiResponse["couponCodes"]) => {
    if (isEmpty(couponCodes)) {
        return { savings: 0, count: 0 };
    }
    const { savings, count } = couponCodes.reduce(
        (acc: { savings: number; count: number }, coupon: GiftingCouponCode) => {
            if (coupon?.savings && isValidNumber(coupon?.savings)) {
                return { savings: acc.savings + Number(coupon?.savings), count: acc.count + 1 };
            }
            return acc;
        },
        { savings: 0, count: 0 },
    );
    return { savings: Math.floor(savings), count };
};

export const showCouponCodesListInGiftingCartSavingsCorner = (applyCouponWidgetData?: GiftingApplyCouponWidgetInfo) => {
    const couponCodes = applyCouponWidgetData?.coupon_codes || [];
    return !isEmpty(couponCodes) && couponCodes.length > 1;
};

export const buildGiftingExtraParamsForSavingsCornerAnalytics = (
    reviewedCart: GiftingCartApiResponse,
): CouponsAnalyticsExtraParams => {
    return {
        restaurantId: reviewedCart?.restaurant_details?.id || "",
        cartId: reviewedCart?.cartId || "",
        originalCartId: reviewedCart?.originalCartId || "",
    };
};

export const buildGiftingCouponCodeList = (reviewedCart: GiftingCartApiResponse): CouponsAnalyticsExtraContext => {
    return {
        couponList: reviewedCart?.couponCodes || [],
    };
};

export const buildGiftingCouponAnalyticsPayload = ({
    couponId,
    coupon,
    isSelecting,
    isApplied,
}: {
    couponId: string;
    coupon: CouponInfo;
    isSelecting: boolean;
    isApplied: boolean;
}) => {
    return {
        offerId: couponId,
        couponCode: coupon.title,
        isApplicable: coupon.applicable || false,
        applicabletype: coupon.businessLine,
        isSelected: isSelecting,
        isApplied: isApplied,
        description: coupon.description || "",
    };
};

export const buildApplyCouponClickAnalyticsPayload = ({
    coupons,
    appliedCouponCodes,
    savingsText,
    couponApplied,
}: {
    coupons?: ICouponCard[] | null;
    appliedCouponCodes: IAppliedCouponCodes[];
    savingsText: string;
    couponApplied: boolean;
}) => {
    return {
        appliedOfferIdList: getAppliedOfferIds(appliedCouponCodes, coupons),
        appliedCouponList: appliedCouponCodes,
        savingsCallout: savingsText,
        ctaText: couponApplied ? CTA_TEXT.GOT_IT : CTA_TEXT.APPLY,
    };
};
